import React, { Component } from "react";
import SharePointApp from "../models/SharePointApp";
import { sharePointAppKey } from "../utils/Utils";
import { CommandBarButton, ComboBox, IContextualMenuProps } from "office-ui-fabric-react";
import TableCell from "./TableCell";
import { GetSharePointAppVersions  } from "../utils/SharePointAppVersionUtils";
import GroundFloorDurableFunctionInstanceStatus from "./GroundFloorDurableFunctionInstanceStatus";
import SharePointPackage from "../models/SharePointPackage";

interface SharePointAppListProps {
    title?: string;
    sharePointApps: SharePointApp[];
    editable: boolean;
    availableSharePointPackages?: SharePointPackage[];
    onSave?: (sharePointAppChanges: SharePointApp[]) => Promise<boolean>
}

interface SharePointAppListState {
    isEditing: boolean;
    areButtonDisabled: boolean;
    sharePointAppChanges: SharePointApp[];
}

export default class SharePointAppList extends Component<SharePointAppListProps, SharePointAppListState>
{
    constructor(props: SharePointAppListProps) {
        super(props);

        this.state = {
            isEditing: false,
            areButtonDisabled: false,
            sharePointAppChanges: []
        }
    }

    async componentDidUpdate(prevProps: Readonly<SharePointAppListProps>, prevState: Readonly<SharePointAppListState>, snapshot?: any): Promise<void> {
        // When sharepoint apps update copy values into our editable list
        if (this.props.sharePointApps != prevProps.sharePointApps) {
            this.CopyInstalledSharePointApps()           
        }
    }

    async componentDidMount() {
    }


    private CopyInstalledSharePointApps() {
        this.setState({
            sharePointAppChanges: this.DeepCopySharepointApps(this.props.sharePointApps)
        })
    }

    private DeepCopySharepointApps(apps: SharePointApp[]): SharePointApp[] {

        return JSON.parse(JSON.stringify(apps)) as SharePointApp[];
    }

    private getAvaliblePackagesForAdd(): IContextualMenuProps {
        var appsToAdd: SharePointApp[] = [];
        var appsThatAreAlreadyAdded = this.state.sharePointAppChanges;
                
        this.props.availableSharePointPackages?.forEach(p => {
            // Check if app not already in use
            if (!appsToAdd.find(a => a.id.toLocaleUpperCase() === p.id.toLocaleUpperCase()) && !appsThatAreAlreadyAdded.find(a => a.id.toLocaleUpperCase() === p.id.toLocaleUpperCase())) {
                appsToAdd.push(p as SharePointApp);
            }
        })

        var menuProps = {
            shouldFocusOnMount: true,
            items: appsToAdd.map(app => {
                return {
                    key: app.id.toLocaleUpperCase(),
                    text: app.name,
                    onClick: () => {
                        this.state.sharePointAppChanges.push(app);
                        this.setState({ sharePointAppChanges: this.state.sharePointAppChanges })
                    }
                }
            }),
        };

        if (menuProps.items.length < 1) {
            menuProps.items.push({
                key: "No apps available",
                text: "No apps available",
                onClick: () => { }
            })
        }

        return menuProps;
    }

    private async SaveChanges(): Promise<void> {

        if (this.props.onSave) {
            this.setState({ areButtonDisabled: true });

            var changes = this.state.sharePointAppChanges.filter(changes => this.props.sharePointApps.filter(existing => existing.id === changes.id && existing.version === changes.version).length === 0)

            await this.props.onSave(changes);
            this.setState({ areButtonDisabled: false });
        }

        this.setState({
            isEditing: false
        })
    }
  
    private updateManagedPackageVersion(packageId: string, version: string | number | undefined) {
        var packageIndex = this.state.sharePointAppChanges.findIndex(app => app.id === packageId)

        var managedAppsCopy = this.state.sharePointAppChanges;
        managedAppsCopy[packageIndex].version = version?.toString();

        this.setState({ sharePointAppChanges: managedAppsCopy })        
    }

    private buildRow(sharePointApp: SharePointApp): JSX.Element {
        return (
            <div className="cdb-table__row" key={sharePointAppKey(sharePointApp)}>
                <div className="cdb-table__cell flex-item--flex-4">
                    <span>{sharePointApp.name}</span>
                </div>
                <div className="cdb-table__cell flex-item--flex-4">
                    {                        
                        <ComboBox
                            disabled={ !this.state.isEditing }
                            selectedKey={this.state.sharePointAppChanges?.filter(app => app.id === sharePointApp.id)[0]?.version ?? "Latest"}
                            allowFreeform
                            options={GetSharePointAppVersions(sharePointApp.id, this.props.availableSharePointPackages)}
                            onChange={(e, option) => this.updateManagedPackageVersion(sharePointApp.id, option?.key)}
                        /> 
                    }
                </div>
                <TableCell to={`/cdb-admin/workloads/${sharePointApp.workload?.durableFunctionInstanceId}`} className="flex-item--flex-4" enableLink={sharePointApp.workload != null}>
                    <span>{sharePointApp.workload?.workloadName}</span>
                    <span className={"cdb-table__extra-detail"}>{sharePointApp.workload?.durableFunctionInstanceId}</span>
                </TableCell>
                <GroundFloorDurableFunctionInstanceStatus durableFunctionInstance={sharePointApp.workload} flex="4">
                </GroundFloorDurableFunctionInstanceStatus>
                <div className="cdb-table__cell flex-item--flex-4">
                    <span>{sharePointApp.observed?.version}</span>
                    <span className={"cdb-table__extra-detail"}>{sharePointApp.observed?.lastObserved}</span>
                </div>
                <div className="cdb-table__cell flex-item--flex-4">
                    <span>{sharePointApp.observed?.errorMessage}</span>
                </div>
            </div>
        );
    }

    private getButtons() {
        return (
            <div>
                {
                    this.props.editable && !this.state.isEditing &&
                    <CommandBarButton
                        iconProps={{ iconName: "edit" }}
                        text="Edit"
                        disabled={this.state.areButtonDisabled}
                        onClick={() => {
                            this.setState({ isEditing: true })
                        }}
                    />
                }
                {
                    this.props.editable && this.state.isEditing &&
                    <CommandBarButton
                        iconProps={{ iconName: "add" }}
                        text="Add"
                        disabled={this.state.areButtonDisabled}
                        menuProps={this.getAvaliblePackagesForAdd()}
                    />
                }
                {
                    this.props.editable && this.state.isEditing &&
                    <CommandBarButton
                        iconProps={{ iconName: "cancel" }}
                        text="Cancel"
                        disabled={this.state.areButtonDisabled}
                        onClick={() => {
                            this.CopyInstalledSharePointApps()                         
                            this.setState({ isEditing: false })
                        }}
                    />
                }
                {
                    this.props.editable && this.state.isEditing &&
                    <CommandBarButton
                        iconProps={{ iconName: "save" }}
                        text="Save"
                        disabled={this.state.areButtonDisabled}
                        onClick={async () => await this.SaveChanges()}
                    />
                }
            </div>
        );
    }

    public render() {
        const rows = this.state.sharePointAppChanges?.map((sharePointApp) => this.buildRow(sharePointApp));

        return (
            <div className="cdb-table">
    
                <div className="card__header">
                    <span className={"card__title"}>{this.props.title ? this.props.title : ""}</span>
                    {this.getButtons()}
                    {this.props.children}
                </div>
    
                <div className="cdb-table__row cdb-table__row--header">
                    <div className="cdb-table__cell flex-item--flex-4">Name</div>
                    <div className="cdb-table__cell flex-item--flex-4">Target Version</div>
                    <div className="cdb-table__cell flex-item--flex-4">Workload</div>
                    <div className="cdb-table__cell flex-item--flex-4">Workload Status</div>
                    <div className="cdb-table__cell flex-item--flex-4">Last Observed Version</div>
                    <div className="cdb-table__cell flex-item--flex-4">Error Messages</div>
                </div>
    
                {rows}
            </div>
        );
    }
}